import { Cake, Dna, Mars, Pencil, Venus } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { PublicPatient, Region } from '@snootsvet/shared/portal'

import { SpeciesIcon } from '@/customer/components/pet/species-icon'
import { Card, CardAction, CardContent, CardHeader } from '@/customer/components/ui/card'
import { formatDob } from '@/customer/lib/formatters'

/**
 * Pre-subscription preview of a pet: species, sex, breed, DOB.
 *
 * Rendered on the pre-checkout confirmation page alongside a
 * `SelectedSubscriptionPlanCard`, giving the member a "here's who
 * you're setting up and here's the plan" pair before they hit Stripe.
 * Live membership state is not this card's job - use `PetHeroCard`
 * for surfaces that reflect an active subscription.
 */
export function PetPreviewCard({
  pet,
  region,
  editHref,
  className,
}: {
  pet: PublicPatient
  /** Locale driving DOB formatting. */
  region: Region
  /**
   * When set, renders a small pencil affordance in the card heading
   * that links to the pet's edit page.
   */
  editHref?: string
  className?: string
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <h3 className="text-headline-sm">{pet.name}</h3>
        {editHref ? (
          <CardAction>
            <Link
              to={editHref}
              aria-label={`Edit ${pet.name}`}
              className="inline-flex p-1 text-foreground/70 transition-colors hover:text-foreground"
            >
              <Pencil className="size-4" />
            </Link>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="text-sm font-bold gap-y-2">
        <div className="flex items-center gap-4">
          <p className="flex items-center gap-2">
            <SpeciesIcon species={pet.species} className="size-5 shrink-0" />
            <span>{speciesLabel[pet.species] ?? pet.species}</span>
          </p>
          {pet.sex === 'male' || pet.sex === 'female' ? (
            <p className="flex items-center gap-2">
              {pet.sex === 'male' ? (
                <Mars className="size-4 shrink-0" />
              ) : (
                <Venus className="size-4 shrink-0" />
              )}
              <span>{pet.sex === 'male' ? 'Male' : 'Female'}</span>
            </p>
          ) : null}
        </div>
        {pet.breed ? (
          <p className="flex items-center gap-2">
            <Dna className="size-4 shrink-0" />
            <span>{pet.breed}</span>
          </p>
        ) : null}
        {pet.dateOfBirth ? (
          <p className="flex items-center gap-2">
            <Cake className="size-4 shrink-0" />
            <span>{formatDob(pet.dateOfBirth, region)}</span>
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

const speciesLabel: Record<string, string> = {
  dog: 'Dog',
  cat: 'Cat',
}
